import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { AUTH_STORAGE_KEYS, CALLBACK_RETRY_CONFIG } from '@/shared/lib/constants';
import { LoadingState } from '@/shared/ui/states/LoadingState';

import { useAuthStore } from '@/entities/auth';

import { fetchCurrentUser } from '@/features/auth/api/userApi';

/**
 * OAuth 콜백 페이지
 * Kong Gateway가 Keycloak 콜백을 처리한 후 이 페이지로 리다이렉트됨
 *
 * 흐름:
 * 1. Keycloak 로그인 완료 → Kong /callback (백엔드)
 * 2. Kong이 토큰 교환 및 세션 생성
 * 3. Kong이 이 페이지로 리다이렉트 (프론트엔드)
 * 4. Kong 세션이 준비될 때까지 대기 및 재시도
 * 5. 세션 확인 후 저장된 경로로 이동
 */
export const CallbackPage = () => {
  const navigate = useNavigate();
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const waitForSession = async () => {
      try {
        console.warn('[CallbackPage] 세션 확인 시도', { attempt: retryCount + 1 });

        const user = await fetchCurrentUser();

        console.warn('[CallbackPage] 세션 확인 성공', { username: user.username });

        useAuthStore.getState().setUser(user);

        sessionStorage.removeItem(AUTH_STORAGE_KEYS.REDIRECT_TIMESTAMP);
        window.history.replaceState({}, document.title, window.location.pathname);
        sessionStorage.removeItem(AUTH_STORAGE_KEYS.KEYCLOAK_STATE);

        const redirectPath = sessionStorage.getItem(AUTH_STORAGE_KEYS.REDIRECT_AFTER_LOGIN);
        sessionStorage.removeItem(AUTH_STORAGE_KEYS.REDIRECT_AFTER_LOGIN);

        const targetPath = redirectPath && redirectPath !== '/' ? redirectPath : '/';
        console.warn('[CallbackPage] 리다이렉트', { targetPath });
        navigate(targetPath, { replace: true });
      } catch {
        console.warn('[CallbackPage] 세션 아직 준비 안됨, 재시도', { attempt: retryCount + 1 });

        if (retryCount >= CALLBACK_RETRY_CONFIG.MAX_RETRIES - 1) {
          console.error('[CallbackPage] 세션 확인 실패 - 최대 재시도 횟수 초과');
          sessionStorage.removeItem(AUTH_STORAGE_KEYS.REDIRECT_TIMESTAMP);
          navigate('/', { replace: true });
          return;
        }

        setRetryCount((prev) => prev + 1);
      }
    };

    const timer = setTimeout(waitForSession, CALLBACK_RETRY_CONFIG.RETRY_INTERVAL);

    return () => clearTimeout(timer);
  }, [navigate, retryCount]);

  return <LoadingState message={`로그인 처리 중... (${retryCount + 1}/${CALLBACK_RETRY_CONFIG.MAX_RETRIES})`} />;
};
